import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { MONTAJ_BAG_NOT_SINIRI, MONTAJ_BAG_TURLERI, type KucukParcaSatiri, type MontajBagTuru, type MontajBagYaniti, type MontajKaydiYaniti, type MontajRol, type MontajSureklilikYaniti } from "@oasis/domain";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { useVeri } from "@/lib/veri";
import { Band, Buton, Cip, Etiket, Hata, Kutu, Modal, alan, etiket, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import {
  SUREKLILIK_UCU,
  adayBlogu,
  adayGosterilirMi,
  beyanAltSatirlari,
  beyanCipi,
  beyanDugmeAdi,
  beyanDugmeleri,
  beyanGovdesi,
  beyanIslemi,
  beyanSn,
  cikanParcaMetni,
  dayanakMetni,
  hucreSatirlari,
  isaretMetni,
  kalemAdi,
  kalemHatAdi,
  kaynakCipleri,
  kayitBirimi,
  kokenUyarisi,
  montajBagKaldir,
  montajBagYaz,
  onDoldurmaRn,
  paketSecenekleri,
  pencereGerekli,
  riserOnerileri,
  rolAlaniGerekli,
  sayacMetni,
  sonucCipi,
  sureklilikKutusuCizilir,
  sureklilikSurumu,
  yenidenBeyanGovdesi,
  type AdayGorunumu,
  type BeyanFormu,
  type HucreGorunumu,
  type SureklilikSatirYaniti,
} from "@/lib/montaj-sureklilik";

/**
 * D-068 (üçüncü teslim): riserın Assembly sayfasındaki "Continuity" kutusu ve "Small parts, side by side (not compared)" kutusu.
 * Yalnız tamamlanmış montaj kaydında çizilir (`sureklilikKutusuCizilir`: rapor türünde girdisi okunmuş kayıt; form türünde imzalı
 * çıktısıyla kapanmış form; plan D §5.1); öteki hâllerde bileşen hiçbir şey çizmez. Yerleştiren `Montaj.tsx` ve `MontajForm.tsx`'tir.
 * Veri `GET /api/montaj/:id/sureklilik`; beyan yazılınca ya da kaldırılınca kutu ve (verildiyse `degisti` ile) sayfanın kaydı
 * yeniden okunur: beyan kaydın "Remove" düğmesini de belirler.
 *
 * Her ana kalem (ana gövde ve dört hat) için üç bilgi yan yana durur: OEM kaydı · sökümde çıkan · montajda takılan. Sonuç sunucuda
 * HESAPLANIR, saklanmaz. Aday yalnız GÖSTERİLİR; sistem hiçbir bağı kendiliğinden kurmaz. Bağ yalnız `mrb` yetkili kişinin
 * BEYANIYLA kurulur (düğmeler `Buton islem="mrb"`: yetkisiz kişide pasif ve ipucu). Beyan kimlik yazmaz: riserın Assembly SN'i,
 * komponent SN'leri, MRB bağı ve stok miktarı buradan değişmez. Metin ve kurallar `lib/montaj-sureklilik.ts`'tedir.
 */

/** Bir hücre: ana satır (eş aralıklı; yazımsa gri) ya da gri boş metin, yanında çipler, altında gri satırlar. */
function Hucre({ h }: { h: HucreGorunumu }) {
  return (
    <>
      {h.ana !== null ? <span className={cn("break-words font-mono", h.yazim && "text-ink-3")}>{h.ana}</span> : <span className="text-ink-3">{h.bos}</span>}
      {h.cipler.map((c, n) => <Fragment key={n}> <Etiket tone={c.ton}>{c.metin}</Etiket></Fragment>)}
      {h.alt.map((m, n) => <div key={n} className={cn("break-words text-xs text-ink-3", n === 0 && "mt-0.5")}>{m}</div>)}
    </>
  );
}

/** Aday satırı: kaydın sayfasına bağlantı (söküm ve montaj), rozet ve zaman ya da çelişki cümlesi. */
function AdaySatiri({ a }: { a: AdayGorunumu }) {
  const b = a.baglanti;
  return (
    <li className="break-words">
      {b?.tur === "sokum" ? <Link to="/sokum/$id" params={{ id: b.id }} className="underline">{a.metin}</Link> : b?.tur === "assembly" ? <Link to="/assembly/$rn" params={{ rn: b.rn }} className="underline">{a.metin}</Link> : a.metin}
      {a.rozet && <> <Etiket tone={a.rozet.ton}>{a.rozet.metin}</Etiket></>}
      {a.not && <> <span className={cn("text-xs", a.not.ton === "bad" ? "text-bad" : "text-ink-3")}>{a.not.metin}</span></>}
    </li>
  );
}

/** Satırın altındaki aday bloğu ("Where this serial number is known"); tablo genişliğinde. */
function AdayBlogu({ s }: { s: SureklilikSatirYaniti }) {
  const b = adayBlogu(s);
  return (
    <div className="rounded-md border border-line bg-surface-2 px-3 py-2">
      <div className="text-xs font-medium text-ink-3">{cevir("Where this serial number is known")}</div>
      {b.bulunamadi && <div className="mt-1 text-sm text-ink">{b.bulunamadi}</div>}
      {(b.tam.length > 0 || b.stok.length > 0) && (
        <ul className="mt-1 space-y-1 text-sm text-ink">
          {b.tam.map((a, n) => <AdaySatiri key={`t${n}`} a={a} />)}
          {b.fazla && <li className="text-xs text-ink-3">{b.fazla}</li>}
          {b.stok.map((a, n) => <AdaySatiri key={`s${n}`} a={a} />)}
        </ul>
      )}
      {b.yakin.length > 0 && <ul className="mt-1 space-y-0.5 break-words text-sm text-ink-3">{b.yakin.map((m, n) => <li key={n}>{m}</li>)}</ul>}
      {b.tuzak && <div className="mt-2 text-xs text-warn">{b.tuzak}</div>}
    </div>
  );
}

/** Beyan penceresi: tür başına alanlar; sunucunun hata cümlesi düğmelerin üstünde, pencere açık kalır. */
function BeyanPenceresi({ y, s, tur, formRolu, mesgul, gonder, kapat }: {
  y: MontajSureklilikYaniti;
  s: SureklilikSatirYaniti;
  tur: MontajBagTuru;
  /** formdaki rol harfi; birim sayfanın kaydında bulunamadıysa undefined */
  formRolu: MontajRol | null | undefined;
  mesgul: boolean;
  /** hata cümlesi ya da null (başarı: pencereyi açan kapatır) */
  gonder: (f: BeyanFormu) => Promise<string | null>;
  kapat: () => void;
}) {
  const rolAlani = rolAlaniGerekli(s, formRolu);
  // Ön-doldurma yalnız "başka riserdan" penceresinde ve yalnız KP-55'in üç koşulu tutarsa; kişi "Confirm"e basmadan hiçbir şey yazılmaz.
  const [rn, setRn] = useState(() => (tur === "baska_riser" ? onDoldurmaRn(s) : ""));
  const [rol, setRol] = useState<MontajRol | null>(null);
  const [paketId, setPaketId] = useState("");
  const [not, setNot] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const liste = `bag-riserler-${s.kalem}`;
  const tamam = async () => setHata(await gonder({ rn, rol, paketId, not }));
  // "Same part" penceresinde rol boş bırakılabilir; "From another riser…" penceresinde zorunludur (sunucu cümlesi söyler).
  const roller: (MontajRol | null)[] = tur === "ayni" ? ["C", "K", null] : ["C", "K"];
  return (
    <Modal baslik={beyanDugmeAdi(tur)} onKapat={kapat} kilitli={mesgul}>
      <p className="mb-3 break-words text-sm text-ink-2"><b>{kalemAdi(s)}</b> · <span className="font-mono">{s.takilan?.sn ?? s.takilan?.yazim ?? "—"}</span></p>
      {tur === "baska_riser" && (
        <>
          <label className={etiket} htmlFor={`${liste}-rn`}>{cevir("Riser the part came from")}</label>
          <input id={`${liste}-rn`} list={liste} className={girdi} value={rn} autoComplete="off" spellCheck={false} disabled={mesgul} onChange={(e) => setRn(e.target.value)} />
          <datalist id={liste}>{riserOnerileri(y).map((r) => <option key={r} value={r} />)}</datalist>
        </>
      )}
      {rolAlani && (tur === "ayni" || tur === "baska_riser") && (
        <div className={tur === "baska_riser" ? "mt-3" : undefined}>
          <div className={etiket}>{cevir("Role of this line")}</div>
          <div className="flex flex-wrap gap-1.5">
            {roller.map((r) => (
              <Buton key={r ?? "bos"} kucuk islem="mrb" tur={rol === r ? "birincil" : "normal"} disabled={mesgul} onClick={() => setRol(r)}>
                {r === "C" ? "Choke" : r === "K" ? "Kill" : cevir("leave empty")}
              </Buton>
            ))}
          </div>
        </div>
      )}
      {tur === "stok" && (
        <>
          <label className={etiket} htmlFor={`${liste}-paket`}>{cevir("Stock package (optional)")}</label>
          <select id={`${liste}-paket`} className={girdi} value={paketId} disabled={mesgul} onChange={(e) => setPaketId(e.target.value)}>
            {paketSecenekleri(y.stokPaketleri, s.adaylar.stok).map((o) => <option key={o.deger} value={o.deger}>{o.etiket}</option>)}
          </select>
        </>
      )}
      <label className={cn(etiket, tur !== "bilinmiyor" && "mt-3")} htmlFor={`${liste}-not`}>{cevir("Note (optional)")}</label>
      <textarea id={`${liste}-not`} rows={2} className={alan} value={not} maxLength={MONTAJ_BAG_NOT_SINIRI} disabled={mesgul} onChange={(e) => setNot(e.target.value)} />
      {tur === "baska_riser" && <p className="mt-3 text-sm text-ink-2">{cevir("Opens an origin entry: Riser Documentation then brings the donor riser's OEM record and inspection report.")}</p>}
      {hata && <Band tip="duvar" className="mt-3">{hata}</Band>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Buton islem="mrb" tur="birincil" mesgul={mesgul} onClick={tamam}>{cevir("Confirm")}</Buton>
        <Buton disabled={mesgul} onClick={kapat}>{cevir("Cancel")}</Buton>
      </div>
    </Modal>
  );
}

/** Beyan hücresi: beyan yokken düğmeler, beyan varken çip, kim ve gün, not, köken uyarısı ve "Remove declaration". */
function BeyanHucresi({ s, beyanAcik, acik, kilitli, mesgul, sec, ac, kaldir, yeniden }: {
  s: SureklilikSatirYaniti;
  beyanAcik: boolean;
  /** "Declare…" ile dört seçenek açıldı */
  acik: boolean;
  kilitli: boolean;
  mesgul: string | null;
  sec: (t: MontajBagTuru) => void;
  ac: () => void;
  kaldir: () => void;
  yeniden: () => void;
}) {
  const g = beyanDugmeleri(s, beyanAcik);
  if (g.gorunum === "yok") return null;
  if (g.gorunum === "beyanli") {
    if (!s.bag) return null;
    const cip = beyanCipi(s.bag);
    const uyari = kokenUyarisi(s.kokenDurumu);
    return (
      <>
        <Etiket tone={cip.ton} className="whitespace-normal">{cip.metin}</Etiket>
        {beyanAltSatirlari(s.bag).map((m, n) => <div key={n} className={cn("break-words text-xs text-ink-3", n === 0 && "mt-1")}>{m}</div>)}
        {uyari && <Band tip="not" className="mt-2">{uyari}</Band>}
        <div className="mt-1 flex flex-wrap gap-1.5">
          {uyari && <Buton kucuk islem="mrb" mesgul={mesgul === `${s.birimId}:yeniden`} disabled={kilitli} onClick={yeniden}>{cevir("Declare again")}</Buton>}
          <Buton kucuk islem="mrb" mesgul={mesgul === `${s.birimId}:kaldir`} disabled={kilitli} onClick={kaldir}>{cevir("Remove declaration")}</Buton>
        </div>
      </>
    );
  }
  const ipucu = g.ipucu ?? undefined;
  if (g.gorunum === "tek" && !acik) return <Buton kucuk islem="mrb" disabled={g.pasif || kilitli} title={ipucu} onClick={ac}>{cevir("Declare…")}</Buton>;
  return (
    <>
      <div className="text-xs text-ink-3">{cevir("not declared")}</div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {MONTAJ_BAG_TURLERI.map((t) => (
          <Buton key={t} kucuk islem="mrb" mesgul={mesgul === `${s.birimId}:${t}`} disabled={g.pasif || kilitli} title={ipucu} onClick={() => sec(t)}>{beyanDugmeAdi(t)}</Buton>
        ))}
      </div>
    </>
  );
}

/** "Small parts, side by side (not compared)": sekiz satır, yazıldığı gibi; karşılaştırma, aday ve beyan yok. Boş hücre "—". */
function KucukParcalar({ parcalar }: { parcalar: readonly KucukParcaSatiri[] }) {
  const bos = <span className="text-ink-3">—</span>;
  return (
    <Kutu baslik={cevir("Small parts, side by side (not compared)")}>
      <p className="mb-2 text-sm text-ink-2">{cevir("Identities are shown as written. Small parts are not compared and not linked.")}</p>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-line">
              {/* Kolon başlığı sözlükten ("Part" → "Parça"; D-068 üçüncü teslim notu); satırlardaki parça adları FM-0012'nin basılı adlarıdır, çevrilmez. */}
              <th className={tabloBas}>{cevir("Part")}</th>
              <th className={tabloBas}>{cevir("OEM record")}</th>
              <th className={tabloBas}>{cevir("Removed (disassembly)")}</th>
              <th className={tabloBas}>{cevir("Fitted (assembly)")}</th>
            </tr>
          </thead>
          <tbody>
            {parcalar.map((p) => (
              <tr key={p.sira} className="border-b border-line last:border-0">
                <td className={tabloHucre}><b className="break-words">{p.ad}</b></td>
                <td className={tabloHucre}>{p.oem.length ? p.oem.map((m, n) => <div key={n} className="break-words font-mono">{m}</div>) : bos}</td>
                <td className={tabloHucre}>{p.cikan.length ? p.cikan.map((c, n) => <div key={n} className="break-words font-mono">{cikanParcaMetni(c)}</div>) : bos}</td>
                <td className={tabloHucre}>
                  {p.takilan.length
                    ? p.takilan.map((t, n) => (
                        <div key={t.birimId} className={cn("break-words", n > 0 && "mt-0.5")}>
                          {t.sokuldu ? <><s className="font-mono text-ink-3">{t.metin}</s> <Etiket>{cevir("removed")}</Etiket></> : <span className="font-mono">{t.metin}</span>}
                          {t.rol && <> <Etiket tone="info">{t.rol === "C" ? "Choke" : "Kill"}</Etiket></>}
                        </div>
                      ))
                    : bos}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Kutu>
  );
}

/**
 * Kutunun yeri: kayıt tamamlanmamışsa (girdi bekleniyor, girdisiz tamamlandı, form açık ya da imzalı çıktısı bekleniyor) hiçbir şey
 * çizilmez ve veri istenmez. `degisti`: beyan işleminden sonra sayfanın kaydını yeniden okuyan geri çağrı (isteğe bağlı).
 */
export function MontajSureklilik({ kayit, degisti }: { kayit: MontajKaydiYaniti; degisti?: () => Promise<unknown> | void }) {
  if (!sureklilikKutusuCizilir(kayit.hal)) return null;
  return <SureklilikKutusu kayit={kayit} degisti={degisti} />;
}

function SureklilikKutusu({ kayit, degisti }: { kayit: MontajKaydiYaniti; degisti?: () => Promise<unknown> | void }) {
  const { veri, hata, yenile } = useVeri<MontajSureklilikYaniti>(SUREKLILIK_UCU.oku(kayit.id));
  // Sayfa kaydı yeni bir okumayla tazelenince (hâl, girdi ya da girdi belgesi değişti) kutu da yeniden okunur.
  const surum = sureklilikSurumu(kayit);
  const ilkSurum = useRef(surum);
  useEffect(() => {
    if (ilkSurum.current === surum) return;
    ilkSurum.current = surum;
    void yenile();
  }, [surum, yenile]);
  /** Süren beyan işlemi: "{birimId}:{tür | kaldir | yeniden}"; öteki düğmeler kilitlenir. */
  const [mesgul, setMesgul] = useState<string | null>(null);
  /** Penceresiz işlemin (aynı parça, kaldırma, yeniden beyan) hata cümlesi; kutunun üstünde. */
  const [islemHatasi, setIslemHatasi] = useState<string | null>(null);
  /** Son beyanın uyarıları (ör. paketin PN'i satırın basılı PN'i değil); kutunun üstünde sarı bantta. */
  const [uyarilar, setUyarilar] = useState<readonly string[]>([]);
  const [pencere, setPencere] = useState<{ kalem: SureklilikSatirYaniti["kalem"]; tur: MontajBagTuru } | null>(null);
  /** "Declare…" ile dört seçeneği açılmış satırlar. */
  const [acik, setAcik] = useState<ReadonlySet<string>>(() => new Set());

  if (hata) return <Kutu baslik={cevir("Continuity")}><Hata mesaj={hata} /></Kutu>;
  if (!veri) return <Kutu baslik={cevir("Continuity")}><p className="text-sm text-ink-3">{cevir("Loading...")}</p></Kutu>;
  const y = veri;
  if (!y.hazir) return <Kutu baslik={cevir("Continuity")}><p className="text-sm text-ink-3">{cevir("Continuity is computed once the assembly input is read.")}</p></Kutu>;

  const kilitli = mesgul !== null;
  /**
   * Beyan işlemi (`beyanIslemi`): yanıtın uyarıları bantta gösterilir; kutu ve sayfanın kaydı her durumda yeniden okunur (işlem düşse
   * de: bayat sayfa ya da kaldırılmış beyan ekranda görünsün; "Remove" düğmesi beyana göre açılır ya da kapanır). Dönen değer hata
   * cümlesidir, başarıda null.
   */
  const yurut = async (anahtar: string, is: () => Promise<MontajBagYaniti>): Promise<string | null> => {
    setMesgul(anahtar);
    setIslemHatasi(null);
    setUyarilar([]);
    const s = await beyanIslemi(is, [yenile, degisti]);
    if (!s.hata) {
      setUyarilar(s.uyarilar);
      setAcik(new Set());
    }
    setMesgul(null);
    return s.hata;
  };
  const birimi = (s: SureklilikSatirYaniti) => kayitBirimi(kayit.satirlar, s.birimId);

  /** Dört beyandan biri seçildi: "Same part" rol sorulmayan birimde penceresiz yazılır; öteki durumlarda pencere açılır. */
  const sec = (s: SureklilikSatirYaniti, tur: MontajBagTuru) => {
    const birimId = s.birimId;
    if (!birimId) return;
    const b = birimi(s);
    if (pencereGerekli(tur, rolAlaniGerekli(s, b ? b.rol : undefined))) {
      setPencere({ kalem: s.kalem, tur });
      return;
    }
    void yurut(`${birimId}:${tur}`, () => montajBagYaz(birimId, beyanGovdesi(tur, beyanSn(s, b)))).then(setIslemHatasi);
  };
  const kaldir = (s: SureklilikSatirYaniti) => {
    const birimId = s.birimId;
    if (!birimId) return;
    void yurut(`${birimId}:kaldir`, () => montajBagKaldir(birimId, beyanSn(s, birimi(s)))).then(setIslemHatasi);
  };
  // Köken satırı Project documents'tan değiştirildiyse kayıtlı beyan aynen yeniden gönderilir; sunucu köken satırını yeniden açar.
  const yeniden = (s: SureklilikSatirYaniti) => {
    const birimId = s.birimId;
    const bag = s.bag;
    if (!birimId || !bag) return;
    void yurut(`${birimId}:yeniden`, () => montajBagYaz(birimId, yenidenBeyanGovdesi(bag, beyanSn(s, birimi(s))))).then(setIslemHatasi);
  };

  const penceredeki = pencere ? (y.satirlar.find((s) => s.kalem === pencere.kalem) ?? null) : null;
  const penceredekiBirim = penceredeki ? birimi(penceredeki) : null;

  return (
    <>
      {islemHatasi && <Hata mesaj={islemHatasi} />}
      {uyarilar.length > 0 && (
        <Band tip="not">
          {/* Sunucu cümleleri (İngilizce): gösterim anında çevrilir. */}
          <ul className="list-disc break-words pl-5">{uyarilar.map((u, n) => <li key={n}>{cevir(u)}</li>)}</ul>
        </Band>
      )}
      <Kutu baslik={cevir("Continuity")} aksiyon={sayacMetni(y.satirlar)}>
        <p className="mb-2 max-w-4xl text-sm leading-relaxed text-ink-2">
          {cevir("What the OEM record gives, what came off at disassembly and what this assembly record reads, side by side. A different serial number is not linked to anything by the system, because one misread digit points to another riser. The Quality Lead declares where the part came from.")}
        </p>
        <div className="mb-3 flex flex-wrap items-center gap-1">
          {kaynakCipleri(y.kaynaklar).map((c) => (c.sokumId ? <Link key={c.metin} to="/sokum/$id" params={{ id: c.sokumId }}><Cip>{c.metin}</Cip></Link> : <Cip key={c.metin}>{c.metin}</Cip>))}
        </div>
        <div className="scroll-thin overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className={tabloBas}>{cevir("Item")}</th>
                <th className={tabloBas}>{cevir("OEM record")}</th>
                <th className={tabloBas}>{cevir("Removed (disassembly)")}</th>
                <th className={tabloBas}>{cevir("Fitted (assembly)")}</th>
                <th className={tabloBas}>{cevir("Result")}</th>
                <th className={tabloBas}>{cevir("Declaration")}</th>
              </tr>
            </thead>
            <tbody>
              {y.satirlar.map((s) => {
                const aday = adayGosterilirMi(s);
                const sonuc = sonucCipi(s);
                const dayanak = dayanakMetni(s.dayanak);
                return (
                  <Fragment key={s.kalem}>
                    <tr className={aday ? undefined : "border-b border-line last:border-0"}>
                      <td className={tabloHucre}><b>{kalemAdi(s)}</b></td>
                      <td className={tabloHucre}><Hucre h={hucreSatirlari("oem", s, y.kaynaklar)} /></td>
                      <td className={tabloHucre}><Hucre h={hucreSatirlari("cikan", s, y.kaynaklar)} /></td>
                      <td className={tabloHucre}><Hucre h={hucreSatirlari("takilan", s, y.kaynaklar)} /></td>
                      <td className={tabloHucre}>
                        <Etiket tone={sonuc.ton}>{sonuc.metin}</Etiket>
                        {dayanak && <div className="mt-1 text-xs text-ink-3">{dayanak}</div>}
                        {s.isaretler.map((i, n) => <div key={n} className="mt-1 break-words text-xs text-ink-2">{isaretMetni(i)}</div>)}
                      </td>
                      <td className={tabloHucre}>
                        <BeyanHucresi
                          s={s}
                          beyanAcik={y.beyanAcik}
                          acik={acik.has(s.kalem)}
                          kilitli={kilitli}
                          mesgul={mesgul}
                          sec={(t) => sec(s, t)}
                          ac={() => setAcik((a) => new Set(a).add(s.kalem))}
                          kaldir={() => kaldir(s)}
                          yeniden={() => yeniden(s)}
                        />
                      </td>
                    </tr>
                    {aday && (
                      <tr className="border-b border-line last:border-0">
                        <td colSpan={6} className="px-3 pb-3"><AdayBlogu s={s} /></td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {y.geriTakilmayan.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-medium text-ink-3">{cevir("Removed at disassembly and not fitted back")}</div>
            {/* Hesaplanan not; kayıt açılmaz. Aynı numara başka riserın montaj kaydında okunuyorsa o kayda bağlanır. */}
            <ul className="mt-1 space-y-0.5 text-sm">
              {y.geriTakilmayan.map((g) => (
                <li key={`${g.kalem}:${g.sn}`} className="break-words">
                  <b>{kalemHatAdi(g.kalem)}</b> <span className="font-mono">{g.sn}</span>{" "}
                  <span className="text-ink-3">
                    ·{" "}
                    {g.montajda.length
                      ? g.montajda.map((m, n) => (
                          <Fragment key={m.kayitId}>
                            {n > 0 && ", "}
                            <Link to="/assembly/$rn" params={{ rn: m.rn }} className="underline">{cevir("on the assembly record of {0}", m.rn)}</Link>
                          </Fragment>
                        ))
                      : cevir("not found on any assembly record")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Band tip="bilgi" className="mt-3">{cevir("Declarations are made by the Quality Lead. A declaration can be removed at any time; nothing is linked without one.")}</Band>
      </Kutu>
      {y.kucukParcalar.length > 0 && <KucukParcalar parcalar={y.kucukParcalar} />}
      {pencere && penceredeki?.birimId && (
        <BeyanPenceresi
          y={y}
          s={penceredeki}
          tur={pencere.tur}
          formRolu={penceredekiBirim ? penceredekiBirim.rol : undefined}
          mesgul={mesgul !== null}
          kapat={() => setPencere(null)}
          gonder={async (f) => {
            const birimId = penceredeki.birimId;
            if (!birimId) return null;
            const mesaj = await yurut(`${birimId}:${pencere.tur}`, () => montajBagYaz(birimId, beyanGovdesi(pencere.tur, beyanSn(penceredeki, penceredekiBirim), f)));
            if (!mesaj) setPencere(null);
            return mesaj;
          }}
        />
      )}
    </>
  );
}
